import React, { useMemo, useRef, useState, useCallback } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ExpandedState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Link,
  HStack,
  VStack,
  IconButton,
  Tooltip,
  Checkbox,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Button,
} from '@chakra-ui/react'
import { ExternalLinkIcon, ChevronUpIcon, ChevronDownIcon, ChevronRightIcon, ViewIcon } from '@chakra-ui/icons'
import { unescapeText } from '../../utils/text'
import { colors } from '../../theme'
import { STRINGS } from '../../resources'
import type { Vulnerability, Severity } from '../../types/vulnerability'
import styles from './VulnerabilityTable.module.css'

export type RowDensity = 'compact' | 'normal' | 'comfortable'

interface VulnerabilityTableProps {
  data: Vulnerability[]
  selectedIds?: Set<string>
  onSelectionChange?: (selectedIds: Set<string>) => void
  rowDensity?: RowDensity
}

const columnHelper = createColumnHelper<Vulnerability>()

// Use shared colorScheme from design tokens
const SEVERITY_BADGE_COLORS = colors.severityColorScheme

const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
}

// Helper to extract attack vector from risk factors
function getAttackVector(riskFactors: string[]): string | null {
  for (const factor of riskFactors) {
    const lower = factor.toLowerCase()
    if (lower.includes('attack') && lower.includes('network')) return STRINGS.ATTACK_VECTOR.NETWORK
    if (lower.includes('attack') && lower.includes('local')) return STRINGS.ATTACK_VECTOR.LOCAL
    if (lower.includes('attack') && lower.includes('adjacent')) return STRINGS.ATTACK_VECTOR.ADJACENT
    if (lower.includes('attack') && lower.includes('physical')) return STRINGS.ATTACK_VECTOR.PHYSICAL
  }
  return null
}

// Helper to check if exploit exists
function hasExploit(riskFactors: string[]): boolean | null {
  for (const factor of riskFactors) {
    const lower = factor.toLowerCase()
    if (lower.includes('exploit')) {
      // Check for positive indicators
      if (lower.includes('available') || lower.includes('true') || lower.includes('yes') || lower.includes('exists')) {
        return true
      }
      // Check for negative indicators
      if (lower.includes('none') || lower.includes('false') || lower.includes('no')) {
        return false
      }
      // If just "exploit" exists as a factor, assume it means exploit exists
      return true
    }
  }
  return null
}

const ROW_HEIGHTS: Record<RowDensity, number> = {
  compact: 36,
  normal: 48,
  comfortable: 60,
}

const OVERSCAN = 20

// Column visibility persistence key
const COLUMN_VISIBILITY_KEY = 'vuln-table-column-visibility'

function loadColumnVisibility(): VisibilityState {
  try {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_KEY)
    return saved ? JSON.parse(saved) : {}
  } catch {
    return {}
  }
}

function saveColumnVisibility(state: VisibilityState) {
  localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(state))
}

export function VulnerabilityTable({
  data,
  selectedIds = new Set(),
  onSelectionChange,
  rowDensity = 'normal',
}: VulnerabilityTableProps) {
  const rowHeight = ROW_HEIGHTS[rowDensity]
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'severity', desc: false },
  ])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(loadColumnVisibility)
  const [expanded, setExpanded] = useState<ExpandedState>({})

  // Persist column visibility changes
  const handleColumnVisibilityChange = useCallback((updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
    setColumnVisibility((old) => {
      const newState = typeof updater === 'function' ? updater(old) : updater
      saveColumnVisibility(newState)
      return newState
    })
  }, [])

  const selectedBg = useColorModeValue('blue.50', 'blue.900')

  const toggleSelection = useCallback((id: string) => {
    if (!onSelectionChange) return
    const newSelection = new Set(selectedIds)
    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }
    onSelectionChange(newSelection)
  }, [selectedIds, onSelectionChange])

  const toggleSelectAll = useCallback(() => {
    if (!onSelectionChange) return
    if (selectedIds.size === data.length) {
      onSelectionChange(new Set())
    } else {
      onSelectionChange(new Set(data.map((v) => v.id)))
    }
  }, [data, selectedIds, onSelectionChange])

  const columns = useMemo(
    () => [
      // Checkbox column for selection
      ...(onSelectionChange ? [{
        id: 'select',
        header: () => (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Checkbox
              isChecked={selectedIds.size === data.length && data.length > 0}
              isIndeterminate={selectedIds.size > 0 && selectedIds.size < data.length}
              onChange={toggleSelectAll}
              aria-label={STRINGS.TABLE.SELECT_ALL}
            />
          </Box>
        ),
        cell: ({ row }: { row: { original: Vulnerability } }) => (
          <Box
            onClick={(e) => e.stopPropagation()}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="100%"
            h="100%"
            minH="40px"
            cursor="pointer"
          >
            <Checkbox
              isChecked={selectedIds.has(row.original.id)}
              onChange={() => toggleSelection(row.original.id)}
              aria-label={`Select ${row.original.cve}`}
            />
          </Box>
        ),
        size: 50,
      }] : []),
      columnHelper.accessor('severity', {
        header: STRINGS.COLUMNS.SEVERITY,
        cell: (info) => {
          const severity = info.getValue()
          const tooltipMap: Record<string, string> = {
            critical: STRINGS.TOOLTIPS.SEVERITY_CRITICAL,
            high: STRINGS.TOOLTIPS.SEVERITY_HIGH,
            medium: STRINGS.TOOLTIPS.SEVERITY_MEDIUM,
            low: STRINGS.TOOLTIPS.SEVERITY_LOW,
          }
          return (
            <Tooltip label={tooltipMap[severity]} hasArrow>
              <Badge
                colorScheme={SEVERITY_BADGE_COLORS[severity]}
                variant="solid"
                className={`${styles.severityBadge} severity-badge ${severity === 'critical' ? 'severity-critical' : ''}`}
              >
                {severity.toUpperCase()}
              </Badge>
            </Tooltip>
          )
        },
        sortingFn: (a, b) =>
          SEVERITY_ORDER[a.original.severity] - SEVERITY_ORDER[b.original.severity],
        size: 100,
      }),
      columnHelper.accessor('cve', {
        header: STRINGS.COLUMNS.CVE,
        cell: (info) => (
          <HStack spacing={1}>
            <Text className={styles.cveText}>
              {info.getValue()}
            </Text>
            <Tooltip label={STRINGS.TOOLTIPS.VIEW_ON_NVD}>
              <Link href={info.row.original.link} isExternal>
                <IconButton
                  aria-label={STRINGS.TOOLTIPS.VIEW_CVE_DETAILS}
                  icon={<ExternalLinkIcon />}
                  size="xs"
                  variant="ghost"
                  className={styles.externalLinkButton}
                />
              </Link>
            </Tooltip>
          </HStack>
        ),
        size: 180,
      }),
      columnHelper.accessor('cvss', {
        header: STRINGS.COLUMNS.CVSS,
        cell: (info) => (
          <Text className={styles.cvssText}>
            {info.getValue().toFixed(1)}
          </Text>
        ),
        size: 70,
      }),
      columnHelper.accessor('packageName', {
        header: STRINGS.COLUMNS.PACKAGE,
        cell: (info) => (
          <Tooltip label={`${info.getValue()}@${info.row.original.packageVersion}`}>
            <Text className={styles.packageText}>
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        size: 150,
      }),
      columnHelper.accessor('kaiStatus', {
        header: STRINGS.COLUMNS.ANALYSIS_STATUS,
        cell: (info) => {
          const status = info.getValue()
          if (!status) {
            return (
              <Badge colorScheme="green" variant="outline">
                {STRINGS.ANALYSIS_STATUS.VALID}
              </Badge>
            )
          }
          if (status === 'ai-invalid-norisk') {
            return (
              <Badge colorScheme="purple" variant="outline">
                {STRINGS.ANALYSIS_STATUS.AI_NO_RISK}
              </Badge>
            )
          }
          if (status === 'invalid - norisk') {
            return (
              <Badge colorScheme="gray" variant="outline">
                {STRINGS.ANALYSIS_STATUS.MANUAL_NO_RISK}
              </Badge>
            )
          }
          return <Text fontSize="sm">{status}</Text>
        },
        size: 130,
      }),
      columnHelper.accessor('riskFactors', {
        id: 'attackVector',
        header: STRINGS.COLUMNS.ATTACK_VECTOR,
        cell: (info) => {
          const factors = info.getValue()
          const vector = getAttackVector(factors)
          if (!vector) return <Text color="gray.400">—</Text>
          const isNetwork = vector === STRINGS.ATTACK_VECTOR.NETWORK
          return (
            <Badge
              size="sm"
              colorScheme={isNetwork ? 'orange' : 'gray'}
              variant={isNetwork ? 'solid' : 'outline'}
            >
              {vector}
            </Badge>
          )
        },
        size: 90,
      }),
      columnHelper.accessor('riskFactors', {
        id: 'exploit',
        header: STRINGS.COLUMNS.EXPLOIT,
        cell: (info) => {
          const factors = info.getValue()
          const exploit = hasExploit(factors)
          if (exploit === null) return <Text color={colors.ui.text.muted.light}>—</Text>
          return exploit ? (
            <Tooltip label={STRINGS.TOOLTIPS.EXPLOIT_AVAILABLE} hasArrow>
              <Text fontSize="xs" color={colors.severity.high} fontWeight="medium">Yes</Text>
            </Tooltip>
          ) : (
            <Tooltip label={STRINGS.TOOLTIPS.NO_EXPLOIT} hasArrow>
              <Text color={colors.ui.text.muted.light}>—</Text>
            </Tooltip>
          )
        },
        size: 60,
      }),
      columnHelper.accessor('fixDate', {
        header: STRINGS.COLUMNS.FIX,
        cell: (info) => {
          const fixDate = info.getValue()
          const hasFix = !!fixDate
          return hasFix ? (
            <Tooltip label={STRINGS.TOOLTIPS.FIX_AVAILABLE} hasArrow>
              <Text fontSize="xs" color={colors.status.active} fontWeight="medium">Yes</Text>
            </Tooltip>
          ) : (
            <Tooltip label={STRINGS.TOOLTIPS.NO_FIX} hasArrow>
              <Text color={colors.ui.text.muted.light}>—</Text>
            </Tooltip>
          )
        },
        size: 60,
      }),
      columnHelper.accessor('groupName', {
        header: STRINGS.COLUMNS.GROUP,
        cell: (info) => (
          <Text fontSize="sm" noOfLines={1}>
            {info.getValue()}
          </Text>
        ),
        size: 120,
      }),
      columnHelper.accessor('imageVersion', {
        header: STRINGS.COLUMNS.IMAGE,
        cell: (info) => (
          <Text fontSize="sm" fontFamily="mono">
            {info.getValue()}
          </Text>
        ),
        size: 100,
      }),
    ],
    [data, selectedIds, onSelectionChange, toggleSelection, toggleSelectAll]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  const { rows } = table.getRowModel()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan: OVERSCAN,
  })

  const virtualRows = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start ?? 0 : 0
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0

  // Get toggleable columns (exclude select checkbox)
  const toggleableColumns = table.getAllColumns().filter(
    (col) => col.id !== 'select' && typeof col.columnDef.header === 'string'
  )

  return (
    <Box>
      {/* Column visibility toggle */}
      <HStack justify="flex-end" mb={2}>
        <Popover placement="bottom-end" closeOnBlur={false}>
          <PopoverTrigger>
            <Button size="xs" variant="ghost" leftIcon={<ViewIcon />}>
              {STRINGS.TABLE.COLUMNS}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="200px">
            <PopoverHeader fontWeight="semibold" fontSize="sm">
              {STRINGS.TABLE.SHOW_COLUMNS}
            </PopoverHeader>
            <PopoverBody>
              <VStack align="stretch" spacing={1}>
                {toggleableColumns.map((column) => (
                  <Checkbox
                    key={column.id}
                    size="sm"
                    isChecked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  >
                    {column.columnDef.header as string}
                  </Checkbox>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>

      <Box
        ref={tableContainerRef}
        className={styles.container}
        borderColor="var(--color-border)"
      >
        <Table size="sm" className={styles.table}>
          <Thead position="sticky" top={0} zIndex={1} bg="var(--color-card)">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id} className={styles.headerRow}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={`${header.getSize()}px`}
                  className={`${styles.headerCell} ${header.column.getCanSort() ? styles.headerCellSortable : ''}`}
                  onClick={header.id !== 'select' ? header.column.getToggleSortingHandler() : undefined}
                  px={header.id === 'select' ? 0 : undefined}
                  textAlign={header.id === 'select' ? 'center' : undefined}
                >
                  {header.id === 'select' ? (
                    flexRender(header.column.columnDef.header, header.getContext())
                  ) : (
                    <HStack className={styles.headerContent}>
                      {header.id === 'cvss' ? (
                        <Tooltip label={STRINGS.TOOLTIPS.CVSS} hasArrow>
                          <Text cursor="help" borderBottomWidth="1px" borderBottomStyle="dotted" borderBottomColor="gray.400">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                        </Tooltip>
                      ) : header.id === 'attackVector' ? (
                        <Tooltip label={STRINGS.TOOLTIPS.ATTACK_VECTOR} hasArrow>
                          <Text cursor="help" borderBottomWidth="1px" borderBottomStyle="dotted" borderBottomColor="gray.400">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                      )}
                      {header.column.getIsSorted() === 'asc' && (
                        <ChevronUpIcon />
                      )}
                      {header.column.getIsSorted() === 'desc' && (
                        <ChevronDownIcon />
                      )}
                    </HStack>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={columns.length} textAlign="center" py={12}>
                <Text color="gray.500">{STRINGS.MISC.NO_RESULTS}</Text>
              </Td>
            </Tr>
          )}
          {paddingTop > 0 && (
            <Tr className={styles.paddingRow}>
              <Td colSpan={columns.length} h={`${paddingTop}px`} p={0} />
            </Tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index]
            const isSelected = selectedIds.has(row.original.id)
            const isOdd = virtualRow.index % 2 === 1
            const isExpanded = row.getIsExpanded()
            const vuln = row.original
            return (
              <React.Fragment key={row.id}>
                <Tr
                  className={`${styles.row} ${styles.rowClickable} ${isOdd ? styles.rowStriped : ''} ${isExpanded ? styles.rowExpanded : ''}`}
                  bg={isSelected ? selectedBg : undefined}
                  _hover={isSelected ? { bg: selectedBg } : undefined}
                  onClick={() => row.toggleExpanded()}
                  h={`${rowHeight}px`}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <Td
                      key={cell.id}
                      w={`${cell.column.getSize()}px`}
                      px={cell.column.id === 'select' ? 0 : undefined}
                      textAlign={cell.column.id === 'select' ? 'center' : undefined}
                      py={rowDensity === 'compact' ? 1 : rowDensity === 'comfortable' ? 4 : 2}
                    >
                      <HStack spacing={1}>
                        {cellIndex === 0 && cell.column.id !== 'select' && (
                          <Box
                            as="span"
                            transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                            transition="transform 0.2s"
                            color="gray.500"
                          >
                            <ChevronRightIcon />
                          </Box>
                        )}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </HStack>
                    </Td>
                  ))}
                </Tr>
                {isExpanded && (
                  <Tr className={`${styles.expandedRow} ${isOdd ? styles.rowStriped : ''}`}>
                    <Td colSpan={row.getVisibleCells().length} p={0}>
                      <Box p={4}>
                        <VStack align="stretch" spacing={3}>
                          {/* Description */}
                          <Box>
                            <Text fontWeight="semibold" fontSize="sm" mb={1}>{STRINGS.DRAWER.DESCRIPTION}</Text>
                            <Text fontSize="sm" color="var(--color-text-secondary)">
                              {vuln.description ? unescapeText(vuln.description) : STRINGS.DRAWER.NO_DESCRIPTION}
                            </Text>
                          </Box>
                          {/* Details Grid */}
                          <HStack spacing={8} flexWrap="wrap">
                            <Box>
                              <Text fontSize="xs" color="var(--color-text-muted)" textTransform="uppercase">{STRINGS.DRAWER.PUBLISHED}</Text>
                              <Text fontSize="sm">{new Date(vuln.published).toLocaleDateString()}</Text>
                            </Box>
                            {vuln.fixDate && (
                              <Box>
                                <Text fontSize="xs" color="var(--color-text-muted)" textTransform="uppercase">{STRINGS.DRAWER.FIX}</Text>
                                <Text fontSize="sm">{new Date(vuln.fixDate).toLocaleDateString()}</Text>
                              </Box>
                            )}
                            <Box>
                              <Text fontSize="xs" color="var(--color-text-muted)" textTransform="uppercase">{STRINGS.COLUMNS.PACKAGE}</Text>
                              <Text fontSize="sm" fontFamily="mono">{vuln.packageName}@{vuln.packageVersion}</Text>
                            </Box>
                            {vuln.riskFactors.length > 0 && (
                              <Box>
                                <Text fontSize="xs" color="var(--color-text-muted)" textTransform="uppercase">{STRINGS.COLUMNS.RISK_FACTORS}</Text>
                                <HStack spacing={1} flexWrap="wrap" mt={1}>
                                  {vuln.riskFactors.map((rf, i) => (
                                    <Badge key={i} size="sm" variant="outline" colorScheme="gray">{rf}</Badge>
                                  ))}
                                </HStack>
                              </Box>
                            )}
                          </HStack>
                          {/* External Link */}
                          <Link href={vuln.link} isExternal color="blue.500" fontSize="sm">
                            {STRINGS.DRAWER.VIEW_ON_NVD} <ExternalLinkIcon mx="2px" />
                          </Link>
                        </VStack>
                      </Box>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            )
          })}
          {paddingBottom > 0 && (
            <Tr className={styles.paddingRow}>
              <Td colSpan={columns.length} h={`${paddingBottom}px`} p={0} />
            </Tr>
          )}
        </Tbody>
        </Table>
      </Box>
    </Box>
  )
}
